/**
 * Simulation output preview — no LiveKit. Delegates to PreviewStaticLayerRenderer
 * so video, image, audio, PDF, and composite layers match player/control-preview rendering.
 */

import { useCallback, useMemo } from 'react';
import { PreviewStaticLayerRenderer } from '@/components/preview/preview-static-layer-renderer';
import type { PreviewLayer } from '@/components/preview/preview-static-layer-renderer';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import type { PlayerEventSettings } from '@/types/preview.types';
import { shouldSimulationLayerLoop } from '@/helpers/simulation-cue-engine.helper';
import { applySequencedPlaybackToPreviewLayers } from '@/helpers/preview/preview-sequenced-playback.helper';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { StillFrameReadyProvider } from '@/components/preview/still-frame-ready-context';
import { buildStillFrameLayersResetKey } from '@/helpers/build-still-frame-layers-reset-key.helper';

export interface SimulationOutputPreviewProps {
  layers: OutputLayer[];
  settings?: PlayerEventSettings | null;
  height?: string | number;
  sequencePlaybackControls?: boolean;
  slideNavigationScope?: import('@/components/preview/preview-presentation-slides-layer.types').PresentationSlideNavigationScope;
  /** Live playout: current on-air item from cueState (play-mode preview without LiveKit). */
  liveOnAirItemId?: string | null;
  /**
   * Freeze videos on the first frame. Next look-ahead uses true; On air / simulation pass false
   * so videos play. Default true for safe look-ahead if omitted.
   */
  stillFrame?: boolean;
  /** Live Current: file videos play at the players' position (layer `playbackStartedAt`). */
  syncFileVideo?: boolean;
  /** Fires when still-frame media under this preview has finished painting (or immediately if none). */
  onStillFrameReadyChange?: (ready: boolean) => void;
  /**
   * Bump when a parent forces not-ready (e.g. new layers fetch) so readiness re-evaluates even if
   * the layer fingerprint is unchanged (common for LiveKit-only compositions).
   */
  stillFrameResetNonce?: number;
  /** Control Next static look-ahead: prefer absolute goto over next/prev for live slide control. */
  preferAbsoluteSlideGoto?: boolean;
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
  showInFrameAudioToggle?: boolean;
}

export function SimulationOutputPreview({
  layers,
  settings,
  height = '100%',
  sequencePlaybackControls = false,
  slideNavigationScope = 'simulation',
  liveOnAirItemId,
  stillFrame = true,
  syncFileVideo = false,
  onStillFrameReadyChange,
  stillFrameResetNonce = 0,
  preferAbsoluteSlideGoto = false,
  previewAudioEnabled,
  onPreviewAudioEnabledChange,
  showInFrameAudioToggle,
}: SimulationOutputPreviewProps) {
  const rows = useFlowControlSimulationCueStore((s) => s.rows);
  const pointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const overridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const handlePlaybackComplete = useFlowControlSimulationCueStore((s) => s.handlePlaybackComplete);

  const simulationOnAirItemId = rows[overridePointer ?? pointer]?.itemId;
  const currentOnAirItemId = liveOnAirItemId ?? simulationOnAirItemId;
  const rowByItemId = useMemo(() => new Map(rows.map((row) => [row.itemId, row])), [rows]);

  const layersWithSequencedPlayback = useMemo(() => {
    return (layers as PreviewLayer[]).map((layer) => {
      if (!layer.itemId || (layer.componentId !== 'imageSequence' && layer.componentId !== 'presentationSlides')) {
        return layer;
      }
      const row = rowByItemId.get(layer.itemId);
      if (!row?.advanceSettings) {
        return layer;
      }
      const [enriched] = applySequencedPlaybackToPreviewLayers([layer], layer.itemId, {
        loopEnabled: row.advanceSettings.loopEnabled,
        dwellMs: row.advanceSettings.dwellMs,
      });
      return enriched ?? layer;
    });
  }, [layers, rowByItemId]);

  const stillFrameResetKey = useMemo(
    () => buildStillFrameLayersResetKey(layersWithSequencedPlayback, stillFrameResetNonce),
    [layersWithSequencedPlayback, stillFrameResetNonce],
  );

  const shouldLoopLayer = useCallback(
    (layer: PreviewLayer) => {
      if (stillFrame) {
        return false;
      }
      if (!layer.itemId || layer.itemId !== currentOnAirItemId) {
        return false;
      }
      return shouldSimulationLayerLoop(rowByItemId.get(layer.itemId));
    },
    [currentOnAirItemId, rowByItemId, stillFrame],
  );

  const onLayerMediaEnded = useCallback(
    (itemId: string) => {
      if (stillFrame || liveOnAirItemId != null) {
        return;
      }
      handlePlaybackComplete(itemId);
    },
    [handlePlaybackComplete, liveOnAirItemId, stillFrame],
  );

  const renderer = (
    <PreviewStaticLayerRenderer
      layers={layersWithSequencedPlayback}
      settings={settings}
      height={height}
      currentOnAirItemId={currentOnAirItemId}
      onLayerMediaEnded={stillFrame ? undefined : onLayerMediaEnded}
      shouldLoopLayer={shouldLoopLayer}
      mediaControls={false}
      stillFrame={stillFrame}
      syncFileVideo={syncFileVideo}
      slideNavigationScope={slideNavigationScope}
      sequencePlaybackControls={stillFrame ? false : sequencePlaybackControls}
      preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
      previewAudioEnabled={previewAudioEnabled}
      onPreviewAudioEnabledChange={onPreviewAudioEnabledChange}
      showInFrameAudioToggle={showInFrameAudioToggle}
    />
  );

  if (!stillFrame) {
    return renderer;
  }

  return (
    <StillFrameReadyProvider resetKey={stillFrameResetKey} onReadyChange={onStillFrameReadyChange}>
      {renderer}
    </StillFrameReadyProvider>
  );
}
